import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import { useSnackbar } from 'notistack';

import BookmarksIcon from '@mui/icons-material/Bookmarks';
import { Box, Button } from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import { isAdmin } from '~/utils/adminPermission';

import OffersService from '../../../services/OffersService';
import { reportError } from '../../../utils/reportError';
import ErrorListDisplay from '../ErrorListDisplay';

import RoomPolicyFormTextWidget from './RoomPolicyFormWidget';
import { buttonMessages, buttonStates } from './states/submitButton';
import MarkdownEditor from './widgets/MarkdownEditor';

export default function RoomPolicyForm({
  schema: propsSchema,
  roomPolicy: initialRoomPolicy,
  idOffer,
  onShowTemplateModalClick,
}) {
  const schema = {
    ...propsSchema,
    properties: {
      room_type_name: {
        title: ' ',
        type: 'string',
      },
      id: {
        title: 'Room Policy ID',
        type: ['integer', 'null'],
      },
      salesforce_id: {
        title: 'Salesforce ID',
        type: ['string', 'null'],
      },
      ...propsSchema.properties,
    },
  };

  const uiSchema = {
    room_type_name: { 'ui:disabled': true },
    room_type_id: { 'ui:widget': 'hidden' },
    id: { 'ui:widget': 'hidden' },
    salesforce_id: { 'ui:widget': 'hidden' },
    description: { 'ui:widget': MarkdownEditor },
  };

  const { enqueueSnackbar } = useSnackbar();

  const [apiErrors, setApiErrors] = useState([]);
  const [roomPolicy, setRoomPolicy] = useState(initialRoomPolicy);
  const [saveState, setSaveState] = useState(buttonStates.default);
  const { user: currentUser } = useCurrentUser();
  const isAdminRole = useMemo(() => isAdmin(currentUser), [currentUser]);

  useEffect(() => {
    setRoomPolicy(initialRoomPolicy);
  }, [initialRoomPolicy]);

  const onChange = useCallback(
    (edit) => {
      setRoomPolicy(edit.formData);
      if (saveState !== buttonStates.default) {
        setSaveState(buttonStates.default);
      }
    },
    [saveState],
  );

  const onSubmit = useCallback(
    async (form) => {
      form.errors = [];
      form.errorSchema = {};

      setSaveState(buttonStates.saving);

      const submitData = JSON.parse(JSON.stringify(form.formData));
      const idRoomPolicy = submitData.id;
      delete submitData['room_type_name'];
      delete submitData['id'];
      delete submitData['salesforce_id'];

      try {
        if (idRoomPolicy) {
          delete submitData['room_type_id'];
          await OffersService.updateRoomPolicy(submitData, idOffer, idRoomPolicy);
        } else {
          await OffersService.createRoomPolicy(submitData, idOffer);
        }
        setSaveState(buttonStates.saved);
        setApiErrors([]);
        enqueueSnackbar('Room policy saved successfully', { variant: 'success' });
      } catch (e) {
        const newApiErrors = [];
        if (e.name === 'ValidationError') {
          e.errors.forEach((err) => {
            newApiErrors.push(err.message);
          });
        }
        setSaveState(buttonStates.failed);
        setApiErrors(newApiErrors);
        reportError(e);
        enqueueSnackbar('Failed to save room policy', { variant: 'error' });
      }
    },
    [enqueueSnackbar, idOffer],
  );

  const validate = useCallback((formData, errors) => {
    return errors;
  }, []);

  let apiErrorsDisplay = null;

  if (apiErrors?.length) {
    apiErrorsDisplay = <ErrorListDisplay messages={apiErrors} />;
  }

  return (
    <Box>
      <Form
        schema={schema}
        formData={roomPolicy}
        validator={validator}
        showErrorList={false}
        liveValidate
        customValidate={validate}
        onSubmit={onSubmit}
        onChange={onChange}
        uiSchema={uiSchema}
        widgets={{
          TextWidget: RoomPolicyFormTextWidget,
        }}
        formContext={{
          hideLabel: true,
        }}
      >
        {apiErrorsDisplay}
        <Box className="button-container" sx={{ display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" className={saveState}>
            {buttonMessages[saveState]}
          </Button>
          <Button
            variant="outlined"
            startIcon={<BookmarksIcon />}
            color="secondary"
            onClick={() => onShowTemplateModalClick('room_policies', roomPolicy.salesforce_id)}
          >
            Show Templates
          </Button>
        </Box>
      </Form>
    </Box>
  );
}
